import { AxiosRequestConfig, AxiosResponse } from "axios";
import axios, { configHeader } from "./axios";

/** Pancake Tower score API (SM-1461). Contract: docs/GAME-CONTRACT.md. */
export const GAME_ID = "pancake-tower";
export type GameId = 'pancake-tower' | 'witch-flight';

export type GamePeriod = "today" | "week" | "all";

export interface GameResult {
  score: number;
  durationMs: number;
  stats: Record<string, number>;
}

export interface PeriodValues { today: number | null; week: number | null; all: number | null }

export interface SubmitScoreResponse { id: number; rank: PeriodValues; best: PeriodValues; moderationStatus?: ModerationStatus; flagged?: boolean; flagReason?: string | null }

export type ModerationStatus = 'clean' | 'review' | 'confirmed';

export interface LeaderboardEntry {
  /** Optional during rolling deploys; missing moderation fields mean clean. */
  scoreId?: number;
  moderationStatus?: ModerationStatus;
  flagged?: boolean;
  flagReason?: string | null;
  rank: number;
  userId: number;
  name: string;
  score: number;
  layers: number;
  achievedAt: string;
}

export interface Leaderboard { game: string; period: GamePeriod; entries: LeaderboardEntry[]; me: LeaderboardEntry | null }

export interface DisqualifiedEntry {
  userId: number;
  name: string;
  scoreId: number;
  moderationStatus: 'confirmed';
}

export interface DisqualifiedLeaderboard {
  game: GameId;
  entries: DisqualifiedEntry[];
  hasMore: boolean;
  nextOffset: number | null;
}

export const getDisqualifiedLeaderboard = async (game: GameId = GAME_ID, offset = 0, limit = 20): Promise<DisqualifiedLeaderboard> => {
  requireAccount();
  const response = await axios.get<DisqualifiedLeaderboard>(
    `/game/scores/disqualified?game=${game}&offset=${offset}&limit=${limit}`, gameConfig());
  return response.data;
};

export interface MyScores {
  plays: number;
  best: number | null;
  todayRank: number | null;
  weekRank: number | null;
  allRank: number | null;
}

/**
 * Only signed-in visitors can record scores. Without a token we must not call
 * the API at all: a 401 would trigger the global refresh → /login redirect,
 * which is wrong on public previews such as /halloween-art.
 */
export const hasGameAccount = (): boolean => {
  try { return !!window.localStorage.getItem("access_token"); } catch { return false; }
};

/** Reads the signed-in user's id from the access-token claims (display only). */
export const currentGameUserId = (): number | null => {
  try {
    const token = window.localStorage.getItem("access_token");
    const payload = token?.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(window.atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return Number.isSafeInteger(json.id) ? json.id : null;
  } catch { return null; }
};

export class NoGameAccountError extends Error {
  code = "no_account" as const;
  constructor() { super("Sign in to save scores"); }
}

/** A game must never bounce the player to /login: failures surface as a small note instead. */
const gameConfig = (): AxiosRequestConfig => ({ ...configHeader(), skipLoginRedirect: true });

const requireAccount = () => {
  if (!hasGameAccount()) throw new NoGameAccountError();
};

const int = (v: unknown) => Math.max(0, Math.round(Number(v) || 0));

export interface GameSession { sessionId: string; expiresAt: string }

export const startGameSession = async (): Promise<GameSession> => {
  requireAccount();
  const response: AxiosResponse<GameSession> = await axios.post("/game/sessions/start", { game: GAME_ID }, gameConfig());
  return response.data;
};

export const submitScore = async (result: GameResult, sessionId: string): Promise<SubmitScoreResponse> => {
  requireAccount();
  const stats = result.stats ?? {};
  const body = {
    game: GAME_ID,
    sessionId,
    score: int(result.score),
    durationMs: int(result.durationMs),
    stats: { layers: int(stats.layers), perfects: int(stats.perfects), bestStreak: int(stats.bestStreak) },
  };
  const response: AxiosResponse<SubmitScoreResponse> = await axios.post("/game/scores", body, gameConfig());
  return response.data;
};

export const getLeaderboard = async (period: GamePeriod, game: GameId = GAME_ID): Promise<Leaderboard> => {
  requireAccount();
  const response: AxiosResponse<Leaderboard> = await axios.get(
    `/game/scores/leaderboard?game=${game}&period=${encodeURIComponent(period)}`, gameConfig());
  return response.data;
};

export const getMyScores = async (game: GameId = GAME_ID): Promise<MyScores> => {
  requireAccount();
  const response: AxiosResponse<MyScores> = await axios.get(`/game/scores/me?game=${game}`, gameConfig());
  return response.data;
};

export interface ScoreModerationResponse { id: number; moderationStatus: ModerationStatus; flagged: boolean; flagReason: string | null }

/** Server authorizes each mutation; the UI role gate is not a security boundary. */
export const setScoreModeration = async (scoreId: number, status: ModerationStatus, reason?: string): Promise<ScoreModerationResponse> => {
  requireAccount();
  const response = await axios.patch<ScoreModerationResponse>(`/game/scores/${scoreId}/flag`,
    { status, ...(reason ? { reason } : {}) }, gameConfig());
  return response.data;
};

export interface WitchSession extends GameSession { seed: number; rulesVersion: 'witch-hardcore-v1' }
export interface WitchReplay { sessionId: string; rulesVersion: 'witch-hardcore-v1'; ticks: number; flapTicks: number[] }
export const startWitchSession = async (): Promise<WitchSession> => {
  requireAccount();
  const response = await axios.post<WitchSession>('/game/sessions/start', { game: 'witch-flight' }, gameConfig());
  return response.data;
};
export const submitWitchScore = async (replay: WitchReplay): Promise<SubmitScoreResponse> => {
  requireAccount();
  const { sessionId, rulesVersion, ticks, flapTicks } = replay;
  const response = await axios.post<SubmitScoreResponse>('/game/witch/scores', { sessionId, rulesVersion, ticks, flapTicks }, gameConfig());
  return response.data;
};
